import React, { Component } from 'react';
import { number, string, func, shape, arrayOf, object, oneOfType, bool, oneOf } from 'prop-types';
import { get, isEqual, head, last, sortBy } from 'lodash';
import ScriptLoader from '../ScriptLoader';
import CircularProgress from '@material-ui/core/CircularProgress';
import { TYPE_STANDARD, GEO_LOCATION_TYPE } from '../constant';

import classNames from 'classnames';
import styles from './styles.scss';

let google;
const CHART_TYPE_GEO = 'geochart';
const CHART_VERSION = 'current';
const CHART_SCRIPT_URL = 'https://www.gstatic.com/charts/loader.js';

const STATUS_LOADED = 'LOADED';
const STATUS_FAILED = 'FAILED';
const STATUS_LOADING = 'LOADING';
const GOOGLE_CHART_OPTIONS = {
  CITY: 'city',
  WORLD: 'world',
  COUNTY: 'county',
  COUNTRY: 'country',
  PROVINCE: 'province',
  CONTINENT: 'continent',
  MARKERS: 'markers',
  REGIONS: 'regions',
  RESOLUTION_COUNTRIES: 'countries',
  RESOLUTION_PROVINCES: 'provinces'
};
const DEFALT_GOOGLE_OPTIONS = {
  region: GOOGLE_CHART_OPTIONS.WORLD,
  dataLevel : GOOGLE_CHART_OPTIONS.COUNTRY,
  displayMode : GOOGLE_CHART_OPTIONS.REGIONS,
  chartResolution : GOOGLE_CHART_OPTIONS.RESOLUTION_COUNTRIES
};

export default class VeritoneGeoChart extends Component {
  static propTypes = {
    className: string,
    mapsApiKey: string,
    autoLoadChartLib: bool,
    geofenceData: arrayOf(
      shape({
        criterial_id: string,
        name: string,
        shortName: string,
        ads: number,
        visits: number,
        visitsOfAds: number,
        lat: number,
        lng: number,
        type: oneOf(Object.values(GEO_LOCATION_TYPE))
      })
    ),
    parentData: arrayOf(shape({
      name: string,
      shortName: string
    })),
    geofenceType: string,
    minColor: string,
    maxColor: string,
    noDataColor: string,
    backgroundColor: string,
    firstErrorMessage: string,
    secondErrorMessage: string,
    legend: oneOfType([object, string]),
    onChartImageReady: func,
    selectedOption: shape({
      value: string,
      label: string
    }),
    isDataLoading: bool,
    includeUnidentifiedLocation: bool
  }

  static defaultProps = {
    legend: null,
    geofenceData: [],
    minColor: '#BBDEFB',
    maxColor: '#1565C0',
    noDataColor: '#F5F5F5',
    backgroundColor: '#FFFFFF',
    firstErrorMessage: 'There was a problem loading the map',
    secondErrorMessage: 'Please try again later',
    autoLoadChartLib: true,
    mapsApiKey: 'AIzaSyBB9Asjfzw1NMla-E8YImPe4loHXl8HqXk',
    selectedOption: {
      value: 'visits',
      label: 'Ad Visits'
    }
  }

  state = { loadingStatus: STATUS_LOADING }

  componentDidMount() {
    if (!this.props.autoLoadChartLib || (window.google && window.google.charts)) {
      this.initChart();
    } else {
      ScriptLoader
      .loadScript(CHART_SCRIPT_URL)
      .then(() => {
        this.initChart();
        return null;
      })
      .catch((error) => {
        this.setState({loadingStatus: STATUS_FAILED});
      });
    }
  }

  componentDidUpdate (prevProps) {
    const { isDataLoading: prevIsDataLoading,
      selectedOption: prevSelectedOptions,
      geofenceData: prevGeofenceData
    } = prevProps;
    const { isDataLoading, selectedOption, geofenceData } = this.props;
    if (
      !isDataLoading && 
      (
        !isEqual(prevGeofenceData, geofenceData) ||
        prevIsDataLoading ||
        !isEqual(prevSelectedOptions, selectedOption)
      )
    ) {
      this.drawGeoChart();
    }
  }

  initTimer;
  initChart = () => {
    if (window.google && window.google.charts) {
      this.initTimer = null;

      google = window.google;
      google.charts.load(
        CHART_VERSION, 
        {
          packages: [CHART_TYPE_GEO],
          mapsApiKey: this.props.mapsApiKey
        }
      );
      
      google.charts.setOnLoadCallback(this.drawGeoChart);
      this.setState({loadingStatus: STATUS_LOADED});
    } else {
      this.initTimer && clearTimeout(this.initTimer);
      this.initTimer = setTimeout(this.initChart, 500);
    }
  }

  //---------------------------------------------------------------------
  //---------------------------Draw Functions----------------------------
  //---------------------------------------------------------------------

  getGoogleChartOptions() {
    const { geofenceData, parentData, geofenceType, includeUnidentifiedLocation } = this.props;
    const {
      CITY,
      WORLD, 
      COUNTY,
      COUNTRY, 
      MARKERS, 
      REGIONS,
      PROVINCE,
      RESOLUTION_PROVINCES, 
      RESOLUTION_COUNTRIES 
    } = GOOGLE_CHART_OPTIONS;

    const isTypeStandard = geofenceType === TYPE_STANDARD;
    const firstEntry = head(geofenceData);
    const locationType = get(firstEntry, 'type');
    const isCountry = locationType === GEO_LOCATION_TYPE.COUNTRY;
    const isProvince = locationType === GEO_LOCATION_TYPE.STATE;
    const isCounty = locationType === GEO_LOCATION_TYPE.COUNTY;
    const countryName = get(parentData, '0.shortName', '');
    const provinceName = get(parentData, '1.type') === GEO_LOCATION_TYPE.STATE && 
                         get(parentData, '1.shortName', '');

    let region = WORLD;
    let dataLevel = COUNTRY;
    let displayMode = REGIONS;
    let chartResolution = RESOLUTION_PROVINCES;

    if(isCountry) {
      chartResolution = RESOLUTION_COUNTRIES;

      //const dataLen = geofenceData.length;

      // If includeUnidentifiedLocation is true, we will include unidentified on the country list, which doesn't count as one country
      // const isSingleCountry = includeUnidentifiedLocation? dataLen < 3 : dataLen < 2;
      
      // if(isSingleCountry) {
      //   const sortedGeoData = sortBy(geofenceData, 'criterial_id');
      //   region = get(last(sortedGeoData), 'shortName', WORLD);
      //    // If user select Unidentified -> we display the world map
      //   if(region === 'ZZ'){ region = WORLD; }
      // }
      
    } else if (isProvince && isTypeStandard) {
      dataLevel = PROVINCE;
      region = countryName;
    } else if (isCounty && isTypeStandard) {
      dataLevel = COUNTY;
      displayMode = MARKERS;
      region = countryName + (provinceName ? `-${provinceName}` : '');
    } else {
      dataLevel = CITY;
      displayMode = MARKERS;
      region = countryName;
      if (countryName === 'US' && isTypeStandard && provinceName) {
        region += `-${provinceName}`;
      }
    }
    
    return {
      dataLevel,
      displayMode,
      chartResolution,
      region
    };
  }

  prepareChartData = () => {
    const {
      minColor,
      maxColor,
      noDataColor,
      backgroundColor,
      geofenceData,
      legend,
      selectedOption
    } = this.props;

    const geoFenceOptions = geofenceData.length && this.getGoogleChartOptions() || DEFALT_GOOGLE_OPTIONS;
    const { dataLevel, region, displayMode, chartResolution } = geoFenceOptions;
    const { label = 'Ad Visits/Ads', value: selectedKey = 'visitsOfAds' } = selectedOption;
    const isCityLevel = dataLevel === GOOGLE_CHART_OPTIONS.CITY;
    const defualtMapHeader = [ dataLevel, label ];
    isCityLevel && defualtMapHeader.unshift('lat', 'lng');
    
    const chartData = geofenceData.reduce((accum, currenEntry) => {
      const { name, lat, lng } = currenEntry;
      const selectedData = currenEntry[selectedKey];
      if (selectedData && !isNaN(selectedData)) {
        if (isCityLevel) {
          accum.push([ lat, lng, name, selectedData ]);
        } else {
          accum.push([ name, selectedData ]);
        }
      }
      return accum;
    }, [defualtMapHeader]);

    // if no data display, we should have title exclude lat & lng
    const dataArray = chartData.length > 1? chartData : [[ dataLevel, label ]];
    const dataTable = google && google.visualization.arrayToDataTable(dataArray);
    //https://developers.google.com/chart/interactive/docs/gallery/geochart
    const options = {
      region,
      legend,
      displayMode,
      resolution: chartResolution,
      enableRegionInteractivity: true,
      backgroundColor: backgroundColor,
      datalessRegionColor: noDataColor,
      colorAxis: {colors: [minColor, maxColor]}
    };

    return {
      options,
      dataTable,
      dataArray
    }
  }

  handleReady = () => {
    const { onChartImageReady } = this.props;
    if (onChartImageReady) {
      const chartImage = this.chart.getImageURI();
      onChartImageReady(chartImage);
    }
  }

  chart;
  drawTimer;
  prevChartData;
  refGeoChart = React.createRef();
  drawGeoChart = () => {
    const {isDataLoading} = this. props;
    if (google && google.visualization && google.visualization.GeoChart && google.visualization.arrayToDataTable && this.refGeoChart.current && !isDataLoading){
      const { options, dataTable, dataArray } = this.prepareChartData();   //Prepare draw options & datatable
      if (isEqual(this.prevChartData, dataArray)) {
        //Skip drawing if there's no update in chart data
        return;
      }

      this.prevChartData = dataArray;
      if (!this.chart) {
        this.chart = dataTable && new google.visualization.GeoChart(this.refGeoChart.current);
        google.visualization.events.addListener(this.chart, 'ready', this.handleReady);
      }
      this.drawTimer = null;                                      //Clear Previous Draw Timeout timer
      this.chart.draw(dataTable, options);                        //Draw GeoChart
    } else {
      // Wait until chart is ready to redraw
      this.drawTimer && clearTimeout(this.drawTimer);
      this.drawTimer = setTimeout(this.drawGeoChart, 500);        //wait for 500ms & try again
    }
  }

  render () {
    const {
      className,
      firstErrorMessage,
      secondErrorMessage,
      isDataLoading
    } = this.props;

    const loadingStatus = this.state.loadingStatus;
    const isFailed = loadingStatus === STATUS_FAILED;
    const isLoaded = loadingStatus === STATUS_LOADED && !isDataLoading;
    const isLoading = loadingStatus === STATUS_LOADING || isDataLoading;

    return (
      <div className={classNames(styles.veritoneGeoChart, className)}>
        <div className={classNames(styles.error, {[styles.hidden]: !isFailed})}>
          <div className={classNames(styles.message)}>
            {firstErrorMessage}
          </div>
          <div className={classNames(styles.message)}>
            {secondErrorMessage}
          </div>
        </div>
        <div className={classNames(styles.chart, {[styles.hidden]: !isLoaded})} ref={this.refGeoChart} />
        <CircularProgress className={classNames({[styles.hidden]: !isLoading})} size={100} thickness={1} />
      </div>
    );
  }
}